import { findBaseEntryConveyor, isInsideBase } from './base';
import { findEntityAt } from './grid';
import type { SimEvent } from './events';
import { tryPushToPort } from './port';
import { machineInputPort } from './routing';
import type { SimState } from './state';
import { samePos } from './types';
import type { GridPos, PacketId } from './types';

/**
 * Returning false is the whole backpressure model: the packet simply stays put until the target
 * can accept it.  `fromPos` identifies the grid cell that owns the packet being delivered — only
 * the base's designated entry conveyor may deliver directly into the base rect.
 */
export function tryDeliverTo(
  state: SimState,
  targetPos: GridPos,
  packetId: PacketId,
  events: SimEvent[],
  fromPos?: GridPos,
): boolean {
  if (isInsideBase(state.base, targetPos)) {
    const entry = findBaseEntryConveyor(state);
    if (!entry || !fromPos || !samePos(entry.position, fromPos)) return false;
    const sink = Object.values(state.sinks)[0];
    if (!sink || !tryPushToPort(sink.input, packetId)) return false;
    events.push({ type: 'packetMoved', packetId, position: sink.position });
    return true;
  }

  const target = findEntityAt(state, targetPos);
  if (!target) return false;

  switch (target.kind) {
    case 'conveyor': {
      const conveyor = state.conveyors[target.id];
      if (conveyor.slot !== null) return false;
      conveyor.slot = packetId;
      events.push({ type: 'packetMoved', packetId, position: conveyor.position });
      return true;
    }
    case 'machine': {
      const machine = state.machines[target.id];
      const port = machineInputPort(state, machine, fromPos);
      if (!port || !tryPushToPort(port, packetId)) return false;
      events.push({ type: 'packetMoved', packetId, position: machine.position });
      return true;
    }
    case 'sink': {
      const sink = state.sinks[target.id];
      if (!tryPushToPort(sink.input, packetId)) return false;
      events.push({ type: 'packetMoved', packetId, position: sink.position });
      return true;
    }
    case 'observable':
    case 'subject':
      return false;
  }
}
